// Human-readable one-line summary of a single visit, e.g.
//   "Visit 1: Selasa, 28 Juli 2026 (16:00 WIB) - FT Intan Rahmawati"
// Used by the "Copy" actions on the Orders list, the Order detail panel, and the
// Appointments (visits) table so the same string lands wherever ops paste it.
//
// Timezone handling mirrors the rest of the app (see formatDateTime in Orders.tsx):
// all formatting goes through `in: tzDate` so date-only values (preferred_date)
// don't drift a day in non-WIB browsers. "WIB" is a fixed label (Asia/Jakarta).

import { type ContextFn, format, type Locale } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import {
	isTimePreference,
	type TimePreference,
	timePreferenceShortWindow,
} from "./time-preference";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Parse to a Date, or null when missing/unparseable — never an Invalid Date. */
function toValidDate(value?: string | null): Date | null {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
}

// Therapist-name prefix per service brand, keyed by the service `code` (see
// config/initializers/seeders/services.rb). Special-tier variants share their
// base brand's code, so both FISIOHOME and FISIOHOME_SPECIAL_TIER resolve to FH.
//   FH → Fisioterapis, PH → Perawat, OT → Okupasi Terapis, WI → Terapis Wicara.
const THERAPIST_PREFIX_BY_SERVICE_CODE: Record<string, string> = {
	FH: "FT",
	PH: "Perawat",
	OT: "OT",
	WI: "TW",
};

export interface VisitLineInput {
	visitNumber: number;
	/** ISO datetime once the visit has a fixed schedule; null while pending. */
	appointmentDateTime?: string | null;
	/** Customer's preferred date, used as a fallback before a fixed schedule. */
	preferredDate?: string | null;
	timePreference?: TimePreference | string | null;
	/** Therapist display name; the brand prefix is added here, don't pre-prefix. */
	therapistName?: string | null;
	/** Service brand code (FH/PH/OT/WI) — selects the therapist-name prefix. */
	serviceCode?: string | null;
}

export interface VisitLineOptions {
	locale?: Locale;
	/** Timezone context (Asia/Jakarta) — pass `tzDate` from `useDateContext`. */
	tzDate?: ContextFn<Date>;
	/** date-fns time token for non-id locales, e.g. "hh:mm a"; id always 24h. */
	timeFormat?: string;
	/** Bound to the "appointments" i18n namespace, for time-preference labels. */
	t?: Translate;
}

/**
 * Format one visit as a single line for clipboard use.
 *
 * Schedule resolution, in order:
 *  1. Fixed `appointmentDateTime` → "Selasa, 28 Juli 2026 (16:00 WIB)".
 *  2. Otherwise `preferredDate` (+ time-preference window when `t` is supplied)
 *     → "Selasa, 28 Juli 2026 (Pagi · 08:00–11:00 WIB)".
 *  3. Neither → "—".
 *
 * The " - FT <name>" suffix is appended only when a therapist name is present.
 */
export function formatVisitLine(
	visit: VisitLineInput,
	options: VisitLineOptions = {},
): string {
	const { locale = idLocale, tzDate, timeFormat, t } = options;
	const timeToken = locale.code === "id" ? "HH:mm" : (timeFormat ?? "HH:mm");
	const dayToken = "EEEE, dd MMMM yyyy";

	// Guard against malformed values: an Invalid Date makes date-fns `format`
	// throw, which would crash the (synchronous) copy handlers.
	const fixed = toValidDate(visit.appointmentDateTime);
	const preferred = toValidDate(visit.preferredDate);

	let schedule: string;
	if (fixed) {
		const day = format(fixed, dayToken, { locale, in: tzDate });
		const time = format(fixed, timeToken, { locale, in: tzDate });
		schedule = `${day} (${time} WIB)`;
	} else if (preferred) {
		const day = format(preferred, dayToken, {
			locale,
			in: tzDate,
		});
		const window =
			t && isTimePreference(visit.timePreference)
				? timePreferenceShortWindow(visit.timePreference, t, {
						locale,
						timeFormat,
					})
				: null;
		schedule = window ? `${day} (${window} WIB)` : day;
	} else {
		schedule = "—";
	}

	const name = visit.therapistName?.trim();
	const prefix = visit.serviceCode
		? THERAPIST_PREFIX_BY_SERVICE_CODE[visit.serviceCode]
		: undefined;
	// Unknown/missing brand code → fall back to the bare name rather than risk a
	// wrong title (e.g. labelling a Perawat as "FT").
	const therapist = name ? ` - ${prefix ? `${prefix} ` : ""}${name}` : "";

	return `Visit ${visit.visitNumber}: ${schedule}${therapist}`;
}
